// Improved GeneralForm with optimized conditional inputs
import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import {
	mdyToCalendarDate,
	calendarDateToMDY,
	ageFromCalendarDate,
	isMDY,
} from '@/utils/date-format'
import {
	calendarRangeStringToCalendarDates,
	calendarDatesToCalendarRangeString,
	getSummerBoundsCalendarDates,
} from '@leangap/types/utils/date'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DateInput, DateRangePicker, Input, Select, SelectItem } from '@heroui/react'
import { FIELD_LIMITS, stepGeneralSchema, type DbUser } from '@leangap/types'
import type { z } from 'zod'
import { appStore, useAppStore, isApplicationLocked } from '@/store'
import { useStepStatusSync } from '@/store/useStepStatusSync'
// import { formProgress } from '@/utils/form-progress'

// import { useStepCompletionSync } from '@/hooks/useStepCompletionSync'
import CountrySelect from '@/components/form/CountrySelect'
import StepHeader from '@/components/account/StepHeader'
import SectionFormTitles from '@/components/account/SectionFormTitles'
import type { CalendarDate } from '@internationalized/date'
// import { useFormVisitValidation } from '@/hooks/useFormVisitValidation'

import { ConditionalFieldInput } from './general/ConditionalFieldInput'
import { useFormVisitValidation } from '@/hooks/useFormVisitValidation'
import PhoneInput from '@/components/form/PhoneInput'
import NameInput from '@/components/form/NameInput'

type StepGeneralForm = z.infer<typeof stepGeneralSchema>

/** Wrapper: guards on user being loaded to satisfy strict TS */
export default function GeneralStep() {
	const { user } = useAppStore()

	if (!user) return null
	return <GeneralForm user={user} />
}

function GeneralForm({ user }: { user: DbUser }) {
	// Whether the application is locked (Submitted, Admitted, or Not Accepted)
	const isSubmitted = isApplicationLocked(user.application_process)
	// For summer break datepicker bounds
	const { min, max } = getSummerBoundsCalendarDates()
	const toDefaults = useCallback((u: DbUser): StepGeneralForm => {
		// Compute age from born_date if present, else use u.age
		let computedAge = ''
		if (u.born_date) {
			const cal = mdyToCalendarDate(u.born_date)
			computedAge = ageFromCalendarDate(cal)
		} else if (u.age != null) {
			computedAge = String(u.age)
		}
		return {
			studentInfo: {
				email: u.email ?? '',
				name: u.name ?? '',
				last_name: u.last_name ?? '',
				born_date: u.born_date ?? '',
				age: computedAge,
				gender: u.gender ?? '',
				country: u.country ?? '',
				city: u.city ?? '',
				zip_code: u.zip_code ?? '',
				phone: u.phone ?? '',
			},
			highSchool: {
				name: u.school_name ?? '',
				country: u.school_country ?? '',
				city: u.school_city ?? '',
				zip_code: u.school_zip_code ?? '',
				school_summer_period: u.school_summer_period ?? '',
			},
			parent: {
				parent_name: u.parent_name ?? '',
				parent_last_name: u.parent_last_name ?? '',
				parent_email: u.parent_email ?? '',
				parent_phone: u.parent_phone ?? '',
				parent_type_relation: u.parent_type_relation ?? '',
				parent_relation: u.parent_relation ?? '',
			},
			other: {
				has_disability: u.has_disability ?? false,
				disability: u.disability ?? '',
			},
		}
	}, [])

	//  This will validate on blur only but not while typing
	// mode: 'onBlur',
	// reValidateMode: 'onChange',
	const form = useForm<StepGeneralForm>({
		resolver: zodResolver(stepGeneralSchema),
		defaultValues: toDefaults(user),
		mode: 'onChange',
		// reValidateMode: 'onChange',
		criteriaMode: 'all',
	})

	const {
		register,
		watch,
		setValue,
		getValues,
		control,
		formState: { errors, isValid },
		clearErrors,
		trigger,
	} = form

	// (removed a redundant no-op watcher subscription — the real subscription that
	// patches the appStore is created later in the file and includes the proper
	// dependency on `watch`.)

	useFormVisitValidation('general', form)
	useStepStatusSync('general', isValid, form.formState.isDirty, form.formState.isSubmitted)

	const handleClearInput = useCallback(
		(field: keyof StepGeneralForm | string) => {
			// field may be a nested path, cast to any to satisfy UseFormSetValue typing
			// cast via unknown to match the setValue param type without using `any`
			setValue(field as unknown as Parameters<typeof setValue>[0], '', {
				shouldValidate: true,
				shouldDirty: true,
			})
		},
		[setValue]
	)

	// Watch specific fields for conditional logic
	const parentTypeRelation = watch('parent.parent_type_relation')
	const hasDisability = watch('other.has_disability')
	const studentPhone = watch('studentInfo.phone')

	// The "phones must differ" rule reports on parent.parent_phone, but react-hook-form only
	// refreshes the error of the field being edited, so editing the student phone would leave
	// the parent field's error stale. Skip the first run so a first visit stays error-free.
	const studentPhoneSynced = useRef(false)
	useEffect(() => {
		if (!studentPhoneSynced.current) {
			studentPhoneSynced.current = true
			return
		}
		if (!getValues('parent.parent_phone')) return
		void trigger('parent.parent_phone')
	}, [studentPhone, getValues, trigger])

	// Memoize conditional states to prevent unnecessary re-renders
	const isOtherRelationEnabled = useMemo(() => parentTypeRelation === 'Other', [parentTypeRelation])

	const isDisabilityEnabled = useMemo(() => hasDisability === true, [hasDisability])

	// Controlled DateInput value synced from RHF's born_date MM/DD/YYYY
	const [bornCal, setBornCal] = useState<CalendarDate | null>(() =>
		mdyToCalendarDate(getValues('studentInfo.born_date'))
	)

	// Optimized date change handler
	const onBornChange = useCallback(
		(val: CalendarDate | null) => {
			setBornCal(val)
			const mdy = calendarDateToMDY(val)
			const nextAge = ageFromCalendarDate(val)

			// Batch updates to reduce re-renders
			setValue('studentInfo.born_date', mdy, { shouldValidate: true, shouldDirty: true })
			setValue('studentInfo.age', nextAge, { shouldValidate: true, shouldDirty: true })
		},
		[setValue]
	)

	// Optimized selection handlers
	const handleParentRelationChange = useCallback(
		(keys: string | { currentKey?: string } | string[]) => {
			const key = Array.isArray(keys)
				? keys[0]
				: typeof keys === 'string'
					? keys
					: (keys?.currentKey ?? '')

			setValue('parent.parent_type_relation', key, { shouldValidate: true, shouldDirty: true })

			// Clear other relation field when not "Other"
			if (key !== 'Other') {
				setValue('parent.parent_relation', '', { shouldValidate: true, shouldDirty: true })
				clearErrors('parent.parent_relation')
			}
		},
		[setValue, clearErrors]
	)

	const handleDisabilityChange = useCallback(
		(keys: string | { currentKey?: string } | string[]) => {
			const key = Array.isArray(keys)
				? keys[0]
				: typeof keys === 'string'
					? keys
					: (keys?.currentKey ?? '')

			const hasDisability = key === 'true'
			setValue('other.has_disability', hasDisability, { shouldValidate: true, shouldDirty: true })

			// Clear disability field when "No"
			if (!hasDisability) {
				setValue('other.disability', '', { shouldValidate: true, shouldDirty: true })
				clearErrors('other.disability')
			}
		},
		[setValue, clearErrors]
	)

	const handleGenderChange = useCallback(
		(keys: string | { currentKey?: string } | string[]) => {
			const key = Array.isArray(keys)
				? keys[0]
				: typeof keys === 'string'
					? keys
					: (keys?.currentKey ?? '')
			setValue('studentInfo.gender', key, { shouldValidate: true, shouldDirty: true })
		},
		[setValue]
	)

	// Country change handlers
	const handleCountryChange = useCallback(
		(val: string) => {
			setValue('studentInfo.country', val, { shouldValidate: true, shouldDirty: true })
		},
		[setValue]
	)

	const handleSchoolCountryChange = useCallback(
		(val: string) => {
			setValue('highSchool.country', val, { shouldValidate: true, shouldDirty: true })
		},
		[setValue]
	)

	// Push changes → global store for autosave (debounced)

	useEffect(() => {
		const sub = watch((values) => {
			const v = values as StepGeneralForm
			// Only save born_date if it's a valid MM/DD/YYYY format to prevent
			// invalid partial dates from being persisted to the database
			const bornDateToSave = isMDY(v.studentInfo.born_date) ? v.studentInfo.born_date : null
			appStore.patch({
				name: v.studentInfo.name || null,
				last_name: v.studentInfo.last_name || null,
				born_date: bornDateToSave,
				age: v.studentInfo.age === '' ? null : Number(v.studentInfo.age),
				gender: v.studentInfo.gender || null,
				country: v.studentInfo.country || null,
				city: v.studentInfo.city || null,
				zip_code: v.studentInfo.zip_code || null,
				phone: v.studentInfo.phone || null,
				school_name: v.highSchool.name || null,
				school_country: v.highSchool.country || null,
				school_city: v.highSchool.city || null,
				school_zip_code: v.highSchool.zip_code || null,
				school_summer_period: v.highSchool.school_summer_period || null,
				parent_name: v.parent.parent_name || null,
				parent_last_name: v.parent.parent_last_name || null,
				parent_email: v.parent.parent_email || null,
				parent_phone: v.parent.parent_phone || null,
				parent_type_relation: v.parent.parent_type_relation || null,
				parent_relation: v.parent.parent_relation || null,
				has_disability: v.other.has_disability ?? null,
				disability: v.other.disability || null,
			})
		})
		return () => sub.unsubscribe()
	}, [watch])

	// Update local form progress only (no patching status here)
	// useEffect(() => {
	// 	formProgress.set('general', isValid)
	// }, [isValid])

	// // DateRangePicker helpers using shared utils
	// const parseDateRange = useCallback((value: string | undefined | null) => {
	// 	const parsed = parseSchoolDateRange(value)
	// 	if (!parsed) return undefined
	// 	// Convert Luxon DateTime to CalendarDate for the picker
	// 	const toCalendar = (dt: any) => new CalendarDate(dt.year, dt.month, dt.day)
	// 	return { start: toCalendar(parsed.start), end: toCalendar(parsed.end) }
	// }, [])

	// const stringifyDateRange = useCallback((range: { start: CalendarDate, end: CalendarDate } | null | undefined): string => {
	// 	if (!range || !range.start || !range.end) return ''
	// 	const toIso = (d: CalendarDate) => `${d.year.toString().padStart(4, '0')}-${d.month.toString().padStart(2, '0')}-${d.day.toString().padStart(2, '0')}`
	// 	return `${toIso(range.start)}/${toIso(range.end)}`
	// }, [])

	return (
		<>
			<StepHeader
				title="General Information"
				lead={
					<>
						Let's start! We require some information about you and your parents. Don't worry, we
						will keep your information safe in accordance with our{' '}
						<a
							className="text-primary-400 hover:text-primary-500 font-semibold hover:underline"
							href="https://www.leangap.org/privacy"
							target="_blank"
							title="Privacy Policy"
							rel="noreferrer"
						>
							Privacy Policy
						</a>
					</>
				}
			/>
			<form className="mt-8 space-y-12 pb-12">
				{/* --- Student Information --- */}
				<section>
					<SectionFormTitles description="By completing this section, you consent to being contacted via phone and/or email.">
						Student Information
					</SectionFormTitles>

					<div className="mb-6">
						<Input
							label="Email"
							value={user.email}
							isReadOnly
							classNames={{ inputWrapper: 'bg-default-200/60' }}
						/>
						<p className="text-foreground-400 p-1 text-sm">
							If you want to change your email, please contact us.
						</p>
					</div>

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<NameInput
							control={control}
							name="studentInfo.name"
							label="First Name"
							isReadOnly={isSubmitted}
							isClearable
						/>
						<NameInput
							control={control}
							name="studentInfo.last_name"
							label="Last Name"
							isReadOnly={isSubmitted}
							isClearable
						/>
					</div>

					<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
						<DateInput
							isDisabled={isSubmitted}
							isRequired
							label="Birth date"
							value={bornCal ?? undefined}
							onChange={onBornChange}
							isInvalid={!!errors.studentInfo?.born_date}
							errorMessage={errors.studentInfo?.born_date?.message}
						/>

						<Controller
							control={control}
							name="studentInfo.age"
							render={({ field, fieldState }) => (
								<Input
									isReadOnly
									label="Age"
									type="number"
									inputMode="numeric"
									value={field.value ?? ''}
									onChange={field.onChange}
									onBlur={field.onBlur}
									name={field.name}
									ref={field.ref}
									isInvalid={!!fieldState.error}
									errorMessage={fieldState.error?.message}
								/>
							)}
						/>

						<Select
							isDisabled={isSubmitted}
							isRequired
							label="Gender"
							selectedKeys={[getValues('studentInfo.gender')]}
							onSelectionChange={handleGenderChange}
							isInvalid={!!errors.studentInfo?.gender}
							errorMessage={errors.studentInfo?.gender?.message}
						>
							<SelectItem key="">Select...</SelectItem>
							<SelectItem key="Male">Male</SelectItem>
							<SelectItem key="Female">Female</SelectItem>
							<SelectItem key="Non-binary">Non-binary</SelectItem>
						</Select>
					</div>

					<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
						<CountrySelect
							onCountryChange={handleCountryChange}
							defaultValue={typeof user.country === 'string' ? user.country : ''}
							errorMessage={errors.studentInfo?.country?.message}
							isInvalid={!!errors.studentInfo?.country}
							isRequired
							className={isSubmitted ? 'pointer-events-none opacity-60' : undefined}
						/>
						<Controller
							control={control}
							name="studentInfo.phone"
							render={({ field, fieldState }) => (
								<PhoneInput
									label="Phone Number"
									isRequired
									isReadOnly={isSubmitted}
									isDisabled={isSubmitted}
									isClearable
									value={field.value ?? ''}
									onValueChange={field.onChange}
									onBlur={field.onBlur}
									isInvalid={!!fieldState.error}
									errorMessage={fieldState.error?.message}
									onClear={() => handleClearInput('studentInfo.phone')}
								/>
							)}
						/>
					</div>

					<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
						<Input
							isReadOnly={isSubmitted}
							isRequired
							isClearable
							label="City of Residence"
							maxLength={FIELD_LIMITS.city}
							{...register('studentInfo.city')}
							isInvalid={!!errors.studentInfo?.city}
							errorMessage={errors.studentInfo?.city?.message}
							onClear={() => handleClearInput('studentInfo.city')}
						/>
						<Input
							isReadOnly={isSubmitted}
							isRequired
							isClearable
							label="Zip Code of Residence"
							maxLength={FIELD_LIMITS.zipCode}
							{...register('studentInfo.zip_code')}
							isInvalid={!!errors.studentInfo?.zip_code}
							errorMessage={errors.studentInfo?.zip_code?.message}
							onClear={() => handleClearInput('studentInfo.zip_code')}
						/>
					</div>
				</section>

				{/* --- High School --- */}
				<section>
					<SectionFormTitles>High School</SectionFormTitles>

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<Input
							isReadOnly={isSubmitted}
							isRequired
							isClearable
							label="Current High School"
							maxLength={FIELD_LIMITS.schoolName}
							{...register('highSchool.name')}
							isInvalid={!!errors.highSchool?.name}
							errorMessage={errors.highSchool?.name?.message}
							onClear={() => handleClearInput('highSchool.name')}
						/>
						<CountrySelect
							onCountryChange={handleSchoolCountryChange}
							defaultValue={typeof user.school_country === 'string' ? user.school_country : ''}
							errorMessage={errors.highSchool?.country?.message}
							isInvalid={!!errors.highSchool?.country}
							label="High School Country"
							isRequired
							className={isSubmitted ? 'pointer-events-none opacity-60' : undefined}
						/>
					</div>

					<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
						<Input
							isReadOnly={isSubmitted}
							isRequired
							isClearable
							label="High School City"
							maxLength={FIELD_LIMITS.city}
							{...register('highSchool.city')}
							isInvalid={!!errors.highSchool?.city}
							errorMessage={errors.highSchool?.city?.message}
							onClear={() => handleClearInput('highSchool.city')}
						/>
						<Input
							isReadOnly={isSubmitted}
							isRequired
							isClearable
							label="High School Zip Code"
							maxLength={FIELD_LIMITS.zipCode}
							{...register('highSchool.zip_code')}
							isInvalid={!!errors.highSchool?.zip_code}
							errorMessage={errors.highSchool?.zip_code?.message}
							onClear={() => handleClearInput('highSchool.zip_code')}
						/>
					</div>

					<div className="col-span-1 mt-4 w-full md:col-span-2">
						<label className="block font-semibold" htmlFor="school_summer_period">
							When does your school's next summer break start and end?{' '}
							<span className="text-danger text-xs">*</span>
						</label>
						<Controller
							control={control}
							name="highSchool.school_summer_period"
							render={({ field, fieldState, formState }) => {
								const touched = formState.touchedFields?.highSchool?.school_summer_period
								return (
									<DateRangePicker
										isDisabled={isSubmitted}
										id="school_summer_period"
										className="custom-daterangepicker mt-2 w-full"
										errorMessage={touched ? fieldState.error?.message : undefined}
										onChange={(range) => field.onChange(calendarDatesToCalendarRangeString(range))}
										value={calendarRangeStringToCalendarDates(field.value)}
										isInvalid={touched ? fieldState.invalid : false}
										isRequired
										size="lg"
										minValue={min}
										maxValue={max}
										shouldForceLeadingZeros={true}
									/>
								)
							}}
						/>
						<p className="text-foreground-400 p-1 text-sm">
							Last day of school before summer break to first day back after summer break.
						</p>
					</div>
				</section>

				{/* --- Parent / Guardian --- */}
				<section>
					<SectionFormTitles description="By completing this section, you are approving your parent to be contacted by phone and/or email.">
						Parent or Guardian Information
					</SectionFormTitles>

					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<NameInput
							control={control}
							name="parent.parent_name"
							isReadOnly={isSubmitted}
							isClearable
							label="Parent/Guardian First Name"
						/>
						<NameInput
							control={control}
							name="parent.parent_last_name"
							isReadOnly={isSubmitted}
							isClearable
							label="Parent/Guardian Last Name"
						/>
					</div>

					<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
						<Input
							isReadOnly={isSubmitted}
							isRequired
							isClearable
							label="Parent/Guardian Email"
							maxLength={FIELD_LIMITS.email}
							{...register('parent.parent_email')}
							isInvalid={!!errors.parent?.parent_email}
							errorMessage={errors.parent?.parent_email?.message}
							onClear={() => handleClearInput('parent.parent_email')}
						/>
						<Controller
							control={control}
							name="parent.parent_phone"
							render={({ field, fieldState }) => (
								<PhoneInput
									label="Phone Number"
									isRequired
									isReadOnly={isSubmitted}
									isDisabled={isSubmitted}
									isClearable
									value={field.value ?? ''}
									onValueChange={field.onChange}
									onBlur={field.onBlur}
									isInvalid={!!fieldState.error}
									errorMessage={fieldState.error?.message}
									onClear={() => handleClearInput('parent.parent_phone')}
								/>
							)}
						/>
					</div>

					<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
						<Select
							isDisabled={isSubmitted}
							isRequired
							label="Parent/Guardian Relationship"
							selectedKeys={[getValues('parent.parent_type_relation')]}
							onSelectionChange={handleParentRelationChange}
							isInvalid={!!errors.parent?.parent_type_relation}
							errorMessage={errors.parent?.parent_type_relation?.message}
						>
							<SelectItem key="">Select...</SelectItem>
							<SelectItem key="Father">Father</SelectItem>
							<SelectItem key="Mother">Mother</SelectItem>
							<SelectItem key="Other">Other</SelectItem>
						</Select>

						<ConditionalFieldInput
							control={control}
							name="parent.parent_relation"
							label="Other relationship"
							isEnabled={isOtherRelationEnabled && !isSubmitted}
							isRequired={isOtherRelationEnabled && !isSubmitted}
							maxLength={FIELD_LIMITS.relation}
							autoFocus={true}
						/>
					</div>
				</section>

				{/* --- Other --- */}
				<section>
					<SectionFormTitles>Other Information</SectionFormTitles>

					<div className="grid">
						<label className="block font-semibold" htmlFor="has_disability">
							Do you identify as a person with a disability?
						</label>
						<div className="mt-2 flex flex-col gap-4 md:flex-row">
							<Select
								isDisabled={isSubmitted}
								isRequired
								id="has_disability"
								label="Select"
								selectedKeys={[
									typeof user.has_disability === 'boolean' ? String(user.has_disability) : '',
								]}
								onSelectionChange={handleDisabilityChange}
								className="w-full md:max-w-none md:flex-1"
								isInvalid={!!errors.other?.has_disability}
								errorMessage={errors.other?.has_disability?.message}
							>
								<SelectItem key="">Select...</SelectItem>
								<SelectItem key="true">Yes</SelectItem>
								<SelectItem key="false">No</SelectItem>
							</Select>

							<ConditionalFieldInput
								control={control}
								name="other.disability"
								label="Name(s) of Disability or Condition"
								isEnabled={isDisabilityEnabled && !isSubmitted}
								isRequired={isDisabilityEnabled && !isSubmitted}
								maxLength={FIELD_LIMITS.disability}
								autoFocus={true}
								className="w-full md:max-w-none md:flex-1"
							/>
						</div>
					</div>
				</section>
			</form>
		</>
	)
}
